import { useState } from "react";
import { Link } from "react-router-dom";
import { SHOP_SHIPPING_NOTICE, type ShopOrder } from "@gooner/shared";
import { userApi } from "../../lib/api";
import { clearShopCart, pendingCheckout, rememberShopOrder } from "../../lib/shopCart";
import { useSession } from "../../lib/session";
import { useUntilFulfilled } from "../hooks/useUntilFulfilled";
import { Button } from "../ui/primitives";

const GIVE_UP_MS = 45_000;
const EVERY_MS = 2_000;

/** The order's private link: the key rides after `#`, which a browser never sends anywhere. */
export const shopOrderLink = (id: string, key: string) => `${window.location.origin}/shop/orders/${id}#k=${key}`;

/**
 * Where the processor sends a shop buyer back to. Same race as the other landings: the
 * redirect usually beats the webhook, so this asks the server rather than believing the
 * trip back, and running out of patience says "on its way", never "it failed". A guest
 * asks with the order key handed out at checkout, and leaves with a link they can keep.
 */
export function ShopThanks() {
  const user = useSession((s) => s.user);
  const [pending] = useState(pendingCheckout);
  const txn = pending?.txn ?? null;
  const key = pending?.key ?? null;
  const [order, setOrder] = useState<ShopOrder | null>(null);
  const [copied, setCopied] = useState(false);
  const { gaveUp } = useUntilFulfilled({
    enabled: !!txn && (!!user || !!key) && !order,
    everyMs: EVERY_MS,
    giveUpMs: GIVE_UP_MS,
    check: async (live) => {
      const r = await userApi.shopOrderByTxn(txn as string, key);
      if (live() && r.order) {
        // Only now: somebody who backs out of the payment page still has their cart.
        clearShopCart();
        if (key) rememberShopOrder(r.order.id, key);
        setOrder(r.order);
        return true;
      }
    },
  });

  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-16 text-center">
      {order ? (
        <>
          <h1 className="text-xl font-semibold text-zinc-100">Order placed</h1>
          <p className="mt-2 text-sm text-zinc-400">
            {order.items.length > 1 ? `${order.items.length} items` : order.productName}, order {order.id}. Your order page shows when it ships.
          </p>
          <p className="mt-2 text-sm text-zinc-400">{SHOP_SHIPPING_NOTICE}</p>
          {key ? (
            <div className="mt-5 w-full rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel-2)] p-3 text-left">
              <p className="text-sm text-zinc-100">Save this link to check your order later.</p>
              <p className="mt-1 text-xs text-zinc-400">
                It works without an account. Anyone with the link can see the order, so keep it to yourself.
                You can also find the order with its number, {order.id}, and your email.
              </p>
              <Button size="sm" className="mt-2" onClick={() => void copy(shopOrderLink(order.id, key))}>
                {copied ? "✓ Link copied" : "Copy private link"}
              </Button>
            </div>
          ) : null}
          <Link
            to={`/shop/orders/${order.id}`}
            className="mt-6 rounded-lg bg-white/10 px-4 py-2 text-sm text-zinc-200 transition-colors hover:bg-white/20"
          >
            View your order
          </Link>
        </>
      ) : (
        <>
          <h1 className="text-xl font-semibold text-zinc-100">
            {gaveUp || !txn ? "Your order is being confirmed" : "Confirming your payment…"}
          </h1>
          <p className="mt-2 text-sm text-zinc-400">
            {gaveUp || !txn
              ? "This can take a few minutes. If your payment went through, the order will appear under Your orders on this device, or you can find it with your order number and email."
              : "This usually takes a few seconds."}
          </p>
          <p className="mt-2 text-sm text-zinc-400">{SHOP_SHIPPING_NOTICE}</p>
          <Link
            to="/shop/orders"
            className="mt-6 rounded-lg bg-white/10 px-4 py-2 text-sm text-zinc-200 transition-colors hover:bg-white/20"
          >
            Your orders
          </Link>
        </>
      )}
    </div>
  );
}
